import {
  Chart13FReplyChartPayload,
  ChartComponentParams,
  CustomPortfolioReplyChartPayload,
  ReplyChartPayload,
} from "charts/src/types/chart";

import { validateChart13FInstanceParams } from "../13f-chart/validate-chart-13f-instance-params";
import { validateCustomPortfolioInstanceParams } from "../custom-portfolio/validate-custom-portfolio-instance-params";

export const getChartParamsList = (
  payload: ReplyChartPayload
): ChartComponentParams[] => {
  const chartKey = payload.chartKey;
  if (
    chartKey === "13f_investor_holding_cost" ||
    chartKey === "13f_investor_holding_weights" ||
    chartKey === "13f_investor_industrial_distribution" ||
    chartKey === "13f_symbol_capital_flow"
  ) {
    return get13FChartParams({ payload });
  }
  if (
    chartKey === "custom_portfolio_returns" ||
    chartKey === "custom_portfolio_weights"
  ) {
    return getCustomPortfolioChartParams({ payload });
  }
  if (!payload.params.instanceData) {
    return getIndicatorChartParams({ payload });
  } else {
    return getInstanceDataChartParams({ payload });
  }
};

const get13FChartParams = ({
  payload,
}: {
  payload: Chart13FReplyChartPayload;
}) => {
  const {
    chartKey,
    params: { instanceParams, ...restParams },
    ...rest
  } = payload;

  if (!instanceParams || instanceParams.length === 0) return [];

  return instanceParams
    .map((singleParams, index) => ({
      ...rest,
      ...restParams,
      chartKey,
      title: rest.title[index],
      chart13FParams: singleParams,
    }))
    .filter(({ chart13FParams }) =>
      chart13FParams
        ? validateChart13FInstanceParams({
            chartKey,
            instanceParams: chart13FParams,
          })
        : false
    );
};

const getCustomPortfolioChartParams = ({
  payload,
}: {
  payload: CustomPortfolioReplyChartPayload;
}) => {
  const {
    params: { instanceParams, ...restParams },
    ...rest
  } = payload;

  if (!instanceParams || instanceParams.length === 0) return [];

  return instanceParams
    .map((singleParams, index) => ({
      ...rest,
      ...restParams,
      title: rest.title[index],
      customPortfolioParams: singleParams,
    }))
    .filter(({ customPortfolioParams }) =>
      customPortfolioParams
        ? validateCustomPortfolioInstanceParams({
            instanceParams: customPortfolioParams,
          })
        : false
    );
};

const getIndicatorChartParams = ({
  payload,
}: {
  payload: ReplyChartPayload;
}) => {
  const {
    params: { symbols, ...restParams },
    isSeparate,
    ...rest
  } = payload;

  if (!symbols || symbols.length === 0) return [];

  if (isSeparate) {
    return symbols.map((symbol, index) => ({
      ...rest,
      ...restParams,
      symbols: [symbol],
      title: rest.title[index],
    }));
  } else {
    return [{ ...rest, ...restParams, symbols, title: rest.title[0] }];
  }
};

const getInstanceDataChartParams = ({
  payload,
}: {
  payload: ReplyChartPayload;
}): ChartComponentParams[] => {
  const {
    isSeparate,
    title,
    params: { instanceData, instanceLabels, ...restParams },
    ...rest
  } = payload;

  if (!instanceData || instanceData.length === 0) return [];

  return isSeparate
    ? instanceData.map((data, index) => ({
        ...rest,
        ...restParams,
        title: title[index],
        instanceData: [data],
        instanceLabels: [instanceLabels?.[index] ?? ""],
      }))
    : [
        {
          ...rest,
          ...restParams,
          title: title[0],
          instanceData,
          instanceLabels: instanceLabels ?? [""],
        },
      ];
};
